import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Shield, LogOut, Inbox, Search, Mail, Building2,
  RefreshCw, Loader2, Users, Download, Check, BellDot,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceColors = {
  Cybersecurity: "text-[#00FFA3] border-[#00FFA3]/40 bg-[#00FFA3]/5",
  "Cloud Infrastructure": "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/5",
  "AI & Autonomous Agents": "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/5",
};

export default function Admin() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
  }, [user, navigate]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch {
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchLeads();
    // eslint-disable-next-line
  }, [token]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return leads;
    return leads.filter(
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.company?.toLowerCase().includes(q) ||
        l.service?.toLowerCase().includes(q)
    );
  }, [leads, query]);

  const unreadCount = useMemo(() => leads.filter((l) => !l.read).length, [leads]);

  const markRead = async (id) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, read: true } : l)));
    try {
      await axios.patch(`${API}/leads/${id}/read`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      toast.error("Could not update lead. Refreshing.");
      fetchLeads();
    }
  };

  const exportCsv = () => {
    if (leads.length === 0) {
      toast.error("No leads to export.");
      return;
    }
    const headers = ["Name", "Company", "Email", "Service", "Message", "Status", "Received"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) =>
      [l.name, l.company, l.email, l.service, l.message, l.read ? "Read" : "New", l.created_at]
        .map(esc)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cybervets-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Leads exported to CSV.");
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#00FFA3]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="admin-page">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0B0E14]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield size={22} className="text-[#00FFA3]" strokeWidth={1.5} />
            <span className="font-heading font-extrabold text-2xl tracking-tight text-white">
              CYBER<span className="text-[#00FFA3]">VETS</span>
            </span>
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 border border-white/10 px-2 py-1 ml-3">
              Admin
            </span>
          </div>
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); navigate("/login"); }}
            className="inline-flex items-center gap-2 border border-white/10 text-slate-300 hover:text-[#00FFA3] hover:border-[#00FFA3]/40 transition-colors px-4 py-2 text-sm"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#00FFA3]">
              // Lead Pipeline
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mt-3">
              Consultation Requests
            </h1>
          </div>
          <button
            data-testid="admin-refresh"
            onClick={fetchLeads}
            className="inline-flex items-center gap-2 border border-white/10 text-slate-300 hover:text-[#00FFA3] hover:border-[#00FFA3]/40 transition-colors px-4 py-2.5 text-sm self-start md:self-auto"
          >
            <RefreshCw size={15} /> Refresh
          </button>
          <button
            data-testid="admin-export"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 bg-[#00FFA3] text-[#0B0E14] font-bold hover:bg-[#00E5FF] transition-colors px-4 py-2.5 text-sm self-start md:self-auto"
          >
            <Download size={15} /> Export CSV
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
          <div className="bg-[#151921] border border-white/10 p-6">
            <div className="flex items-center gap-2 text-slate-400">
              <Inbox size={16} className="text-[#00FFA3]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest">Total Leads</span>
            </div>
            <div className="font-heading font-bold text-3xl text-white mt-3" data-testid="stat-total">
              {leads.length}
            </div>
          </div>
          <div className="bg-[#151921] border border-[#00FFA3]/30 p-6">
            <div className="flex items-center gap-2 text-slate-400">
              <BellDot size={16} className="text-[#00FFA3]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest">New / Unread</span>
            </div>
            <div className="font-heading font-bold text-3xl text-[#00FFA3] mt-3" data-testid="stat-unread">
              {unreadCount}
            </div>
          </div>
          <div className="bg-[#151921] border border-white/10 p-6">
            <div className="flex items-center gap-2 text-slate-400">
              <Shield size={16} className="text-[#00FFA3]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest">Cybersecurity</span>
            </div>
            <div className="font-heading font-bold text-3xl text-white mt-3">
              {leads.filter((l) => l.service === "Cybersecurity").length}
            </div>
          </div>
          <div className="bg-[#151921] border border-white/10 p-6">
            <div className="flex items-center gap-2 text-slate-400">
              <Users size={16} className="text-[#00E5FF]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest">Cloud / AI</span>
            </div>
            <div className="font-heading font-bold text-3xl text-white mt-3">
              {leads.filter((l) => l.service !== "Cybersecurity").length}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="admin-search"
            className="w-full bg-[#151921] border border-white/10 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#00FFA3] pl-11 pr-4 py-3"
            placeholder="Search by name, email, company, service..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Leads */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-[#00FFA3]" size={28} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-white/10 bg-[#151921] py-24 text-center" data-testid="admin-empty">
            <Inbox size={40} className="text-slate-600 mx-auto" />
            <p className="text-slate-400 mt-4">
              {leads.length === 0 ? "No leads yet. Submissions will appear here." : "No leads match your search."}
            </p>
          </div>
        ) : (
          <div className="border border-white/10 overflow-hidden" data-testid="leads-table">
            {/* header row - desktop */}
            <div className="hidden md:grid grid-cols-12 gap-4 bg-[#05070A] px-6 py-4 font-mono-tech text-[10px] uppercase tracking-widest text-slate-500">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Company</div>
              <div className="col-span-2">Service</div>
              <div className="col-span-2 text-right">Received</div>
            </div>
            {filtered.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-row-${l.id}`}
                className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-t border-white/5 hover:bg-[#151921] transition-colors items-center ${
                  l.read ? "" : "bg-[#00FFA3]/[0.03]"
                }`}
              >
                <div className="md:col-span-3 font-medium text-white flex items-center gap-2">
                  {!l.read && (
                    <span
                      data-testid={`lead-new-badge-${l.id}`}
                      className="shrink-0 font-mono-tech text-[9px] uppercase tracking-widest bg-[#00FFA3] text-[#0B0E14] px-1.5 py-0.5 font-bold"
                    >
                      New
                    </span>
                  )}
                  {l.name}
                </div>
                <div className="md:col-span-3">
                  <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 text-[#00FFA3] hover:text-[#00E5FF] transition-colors text-sm break-all">
                    <Mail size={13} /> {l.email}
                  </a>
                </div>
                <div className="md:col-span-2 text-slate-400 text-sm inline-flex items-center gap-1.5">
                  {l.company ? <><Building2 size={13} className="text-slate-600" /> {l.company}</> : <span className="text-slate-600">—</span>}
                </div>
                <div className="md:col-span-2">
                  <span className={`inline-block border px-2.5 py-1 text-xs ${serviceColors[l.service] || "text-slate-300 border-white/10 bg-white/5"}`}>
                    {l.service}
                  </span>
                </div>
                <div className="md:col-span-2 md:text-right flex md:flex-col md:items-end gap-2 md:gap-1">
                  <span className="font-mono-tech text-xs text-slate-500">{fmtDate(l.created_at)}</span>
                  {!l.read && (
                    <button
                      data-testid={`lead-mark-read-${l.id}`}
                      onClick={() => markRead(l.id)}
                      className="inline-flex items-center gap-1 text-[#00FFA3] hover:text-[#00E5FF] transition-colors text-xs font-mono-tech uppercase tracking-wider"
                    >
                      <Check size={12} /> Mark read
                    </button>
                  )}
                </div>
                {l.message && (
                  <div className="md:col-span-12 text-slate-400 text-sm md:pl-0 border-l-2 md:border-l-0 border-[#00FFA3]/30 pl-3 mt-1 md:mt-2 md:border-t md:border-white/5 md:pt-3">
                    {l.message}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
